import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loader from "../../components/utils/Loader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalculator, faDollarSign, faDownload, faShareAlt, faTimes } from "@fortawesome/free-solid-svg-icons";
import ExamUnionEarningCalculator from "./ExamUnionEarningCalculator";
import QRCode from 'react-qr-code';

const ExamUnionEarningEstimator = () => {
  const authState = useSelector((state) => state.authReducer);
  const navigate = useNavigate();

  const [userId] = useState(authState.user._id);
  const [userName] = useState(authState.user.name);
  const [userCode] = useState(authState.user.think5whyscode);
  const [showQR, setShowQR] = useState(false);
  const qrRef = useRef();

  const [plan, setPlan] = useState(20);
  const [maxLevel, setMaxLevel] = useState(6);
  const [refsPerLevel, setRefsPerLevel] = useState(5);
  const [amount, setAmount] = useState(50);
  const [errorMsg, setErrorMsg] = useState("");
  const [showEarningEstimator, setShowEarningEstimator] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!amount || isNaN(amount) || Number(amount) < 10 || Number(amount) > 99999) {
      setErrorMsg("Please enter a valid number between 10 to 99999 for the amount.");
      return;
    }

    if (!plan) {
      setErrorMsg("Please enter plan.");
      return;
    }

    const planNumber = Number(plan);
    if (planNumber !== 10 && planNumber !== 20) {
      setErrorMsg("Input 10 or 20 only in the plan");
      return;
    }

    setErrorMsg("");
    setShowEarningEstimator(true);
  };

  const handleCancel = () => navigate("/examunion");

  const shareEstimateUrl = `https://www.think5whys.com/examunion-earning-estimator`;
  const shareBuyingUrl = `https://www.think5whys.com/examunion-buy-series?ref=${userCode}`;

  const handleShare = async () => {
  try {
    const shareText = `Think5Whys ExamUnion ValueShare

      I am using this as a learner and benefiting from it. 
      Amount: ₹${amount} 
      Referrals per level: ${refsPerLevel} 
      Levels: ${maxLevel} 
      
      You can also check your earning potential: 
      Estimate Earning here: ${shareEstimateUrl}

      Join ${shareBuyingUrl} 
      with my Referral Code: ${userCode}
    `;

    // convert QR to canvas image
    const svg = qrRef.current.querySelector("svg");
    const svgData = new XMLSerializer().serializeToString(svg);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const img = new Image();
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);

    img.onload = async () => {
      canvas.width = 180;
      canvas.height = 180;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      URL.revokeObjectURL(url);

      canvas.toBlob(async (blob) => {
        const file = new File([blob], "qrcode.png", { type: "image/png" });

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: "ExamUnion ValueShare",
            text: shareText,
            files: [file], // 👈 THIS makes QR visible in WhatsApp
          });
        } else {
          await navigator.clipboard.writeText(shareText);
          alert("QR sharing not supported. Text copied.");
        }
      });
    };

    img.src = url;
  } catch (err) {
    console.error("Share failed:", err);
  }
};

const handlePDF = () => {
  window.print();
};

  return (
    <div className="min-h-screen bg-black py-4 rounded-xl">
      <h3 className="text-lg lg:text-xl font-bold text-yellow-200 text-center">Think5Whys ExamUnion ValueShare</h3>
      <h4 className="text-xs lg:text-sm font-semibold text-pink-200 text-center">
        Pocket Money Income Estimator (Sell valuable learning products)
      </h4>
      <div className="p-2 shadow-md rounded-lg mb-2 flex flex-row justify-center">
        <QRCode value={shareBuyingUrl} size={64} className="border-2 border-blue-100 p-2" />
      </div>
      <h3 className="text-xs lg:text-sm font-semibold text-cyan-200 text-center mb-2">
        {userName} {'  '} {userCode}
      </h3>            
      <div className="shadow-lg rounded-xl bg-white/5 backdrop-blur-sm p-2 max-w-2xl mx-auto w-full border border-blue-500">
        {loading ? (
          <Loader />
        ) : (
          <form className="space-y-2">
            <div className="flex flex-row lg:flex-row justify-between gap-2">
              <label className="text-xs lg:text-sm text-white font-medium">
                This model operates as a product-based direct selling system and is intended to 
                <span className="text-cyan-300 text-sm">comply with the Consumer Protection (Direct Selling) Rules, 2021 </span>
                 and applicable laws in India.
              </label>
            </div>

            <div className="space-y-4">
              <div className="bg-white/10 backdrop-blur-md p-4 rounded-2xl shadow-sm">
                <label htmlFor="amount" className="block text-xs text-white/70 mb-1">
                  Price / Spend 💸
                </label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={amount}
                  min={25}
                  max={9999}
                  step={25}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="e.g. 500"
                  className={`w-full h-12 px-4 rounded-xl text-sm text-black focus:outline-none focus:ring-2 focus:ring-pink-500 transition ${
                    errorMsg ? "border-2 border-blue-500" : "border border-gray-300"
                  }`}
                />
                <p className="text-[10px] text-white/50 mt-1">Min ₹25 • Max ₹9999</p>
              </div>

              <div className="bg-white/10 backdrop-blur-md p-4 rounded-2xl shadow-sm">
                <label htmlFor="maxLevel" className="block text-xs text-white/70 mb-2">
                  Max Level 🚀
                </label>
                <select
                  id="maxLevel"
                  value={maxLevel}
                  onChange={(e) => setMaxLevel(e.target.value)}
                  className="w-full h-12 px-4 rounded-xl text-sm text-black border border-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  {[...Array(6)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      Level {i + 1}
                    </option>
                  ))}
                </select>
              </div>

              {/* References */}
              <div className="bg-white/10 backdrop-blur-md p-4 rounded-2xl shadow-sm">
                <label htmlFor="refsPerLevel" className="block text-xs text-white/70 mb-2">
                  Referrals per Level 🔗
                </label>
                <select
                  id="refsPerLevel"
                  value={refsPerLevel}
                  onChange={(e) => setRefsPerLevel(e.target.value)}
                  className="w-full h-12 px-4 rounded-xl text-sm text-black border border-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500"
                >
                  {[...Array(7)].map((_, i) => (
                    <option key={i + 2} value={i + 2}>
                      {i + 2} refs
                    </option>
                  ))}
                </select>
              </div>

            </div>

            {errorMsg && <p className="text-red-400 text-xs lg:text-sm text-center">{errorMsg}</p>}

            {/* Buttons */}
            <div className="flex flex-wrap justify-center gap-1 mt-1">
              <button
                type="button"
                onClick={handleSubmit}
                className="w-32 text-xs lg:text-sm bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-2 rounded-md transition duration-300"
              >
                <FontAwesomeIcon icon={faCalculator} />
                <span className="px-2">Estimate</span>
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="w-32 text-xs lg:text-sm bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-2 rounded-md transition duration-300"
              >
                <FontAwesomeIcon icon={faShareAlt} />
                <span className="px-2">Share</span>
              </button>
              <button
                type="button"
                onClick={handlePDF}
                className="w-32 text-xs lg:text-sm bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-2 rounded-md transition duration-300"
              >
                <FontAwesomeIcon icon={faDownload} />
                <span className="px-2">Download</span>
              </button>              
              <button
                type="button"
                onClick={handleCancel}
                className="w-32 text-xs lg:text-sm bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-2 rounded-md transition duration-300"
              >
                <FontAwesomeIcon icon={faTimes} />
                <span className="px-4">Home</span>
              </button>
            </div>
            <div
              ref={qrRef}
              style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
            >
              <div className="bg-white p-2">
                <QRCode value={shareBuyingUrl} size={180} />
              </div>
            </div>
            {/* {showQR && (
              <div className="mt-4 text-center border border-blue-400 p-3 rounded-lg bg-black">
                <p className="text-yellow-200 mb-2">Scan to know how much you can earn</p>
                <div className="flex justify-center bg-white p-2 rounded">
                  <QRCode
                    value={shareEstimateUrl}
                    size={180}
                    style={{ height: "auto", maxWidth: "100%", width: "180px" }}
                    viewBox={`0 0 256 256`}
                  />
                </div>
                <p className="text-green-300 text-sm mb-1"> Scan to join using my referral code</p>
                <div className="flex justify-center bg-white p-2 rounded">
                  <QRCode
                    value={shareBuyingUrl}
                    size={180}
                    style={{ height: "auto", maxWidth: "100%", width: "180px" }}
                    viewBox={`0 0 256 256`}
                  />
                </div>

                <p className="text-gray-300 text-xs mt-2 break-all">
                  {shareUrl}
                </p>

                <button
                  type="button"
                  className="mt-2 text-xs text-blue-400 underline"
                  onClick={() => setShowQR(false)}
                >
                  Close
                </button>
              </div>
            )} */}

            {/* Estimated Income Table */}
            {showEarningEstimator && (
              <div className="mt-0 text-center border-t border-blue-400 py-1">
                <p className="text-pink-200 text-xs lg:text-sm mb-1">
                  Estimated earning table for ₹{amount}.00, with {refsPerLevel} referrals per level, under a {plan}% referral incentive plan up to {maxLevel} levels.
                </p>
                { 
                  <ExamUnionEarningCalculator
                    param1={amount}
                    param2={plan}
                    maxLevel={maxLevel}
                    refsPerLevel={refsPerLevel}
                  />
                }
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default ExamUnionEarningEstimator;
